// @flow

import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { TASK_INSTANCE_COMPLETED_STATUS } from 'sling-calendar-sdk/constants';

import type { Shift, TaskInstance } from '../common/typings';
import ButtonContainer from '../common/components/Forms/ButtonContainer';
import Button from '../common/components/Button';
import AnimatedRow from '../common/components/Forms/AnimatedRow';
import ClickableRow from '../common/components//Forms/ClickableRow';
import RowIcon from '../common/components/Forms/RowIcon';
import RowContent from '../common/components/Forms/RowContent';
import FormContainer from '../common/components/Forms/FormContainer';
import FormScreenContainer from '../common/components/ui/FormScreenContainer';
import PaddedContainer from '../common/components/ui/PaddedContainer';
import ContentText from '../common/components/Text/ContentText';
import Label from '../common/components/Text/Label';
import { SMALL_DEVICE_HEIGHT } from '../constants';
import taskIcon from '../assets/svg/icons/task-gray.svg';
import taskGreenIcon from '../assets/svg/icons/task-green.svg';
import arrowRightIcon from '../assets/svg/icons/arrow-right.svg';

const FormFieldsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;

  @media (max-height: ${SMALL_DEVICE_HEIGHT}px) {
    overflow: visible;
  }

  @media (min-height: ${SMALL_DEVICE_HEIGHT + 1}px) {
    margin-top: 40px;
    max-height: ${props => props.deviceHeight * 0.6}px;
    overflow-x: hidden;
  }
`;

const AnimatedFullWidthRow = styled(AnimatedRow)`
  width: 100%;
`;

const RowColumn = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
`;

const ArrowContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const PaddedButtonContainer = styled(PaddedContainer)`
  width: 100%;
`;

type Props = {
  shift: Shift,
  taskPreview: (task: TaskInstance) => void,
  onBack: () => void,
  deviceHeight: number,
  onViewActivated: (ref: Object) => void
};

export default function MultipleTasksFields({
  shift,
  taskPreview,
  onBack,
  deviceHeight,
  onViewActivated
}: Props) {
  const taskInstances = shift.taskInstances || [];
  const ref = useRef();

  useEffect(() => {
    onViewActivated(ref);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <FormScreenContainer ref={ref}>
      <FormContainer>
        <FormFieldsContainer deviceHeight={deviceHeight}>
          <PaddedContainer>
            {taskInstances.map((taskInstance, index) => {
              const isCompleted = taskInstance.status === TASK_INSTANCE_COMPLETED_STATUS;
              const delay = 0.2 * index;
              return (
                <ClickableRow
                  key={taskInstance.id}
                  delay={delay}
                  onClick={() => taskPreview(taskInstance)}>
                  <AnimatedFullWidthRow
                    borderTop
                    borderBottom={index === taskInstances.length - 1}
                    delay={delay}>
                    <RowIcon src={isCompleted ? taskGreenIcon : taskIcon} />
                    <RowContent>
                      <ArrowContainer>
                        <RowColumn>
                          <Label>Task</Label>
                          <ContentText>{taskInstance.title}</ContentText>
                        </RowColumn>
                        <img src={arrowRightIcon} alt="Preview task" />
                      </ArrowContainer>
                    </RowContent>
                  </AnimatedFullWidthRow>
                </ClickableRow>
              );
            })}
          </PaddedContainer>
        </FormFieldsContainer>
        <PaddedButtonContainer>
          <ButtonContainer delay={0.2 * taskInstances.length}>
            <Button label="Back" buttonTheme="grey" onPress={onBack} withMargin />
          </ButtonContainer>
        </PaddedButtonContainer>
      </FormContainer>
    </FormScreenContainer>
  );
}
